/**
 * Shared hours-range math for the deal estimate surfaces. The workspace variance band
 * (`EstimateRange`, lowerPct/upperPct on a 0-100 scale) turns a base hours figure into a low | high
 * range. Returns null when no band is configured ("if configured"), so callers can fall back to
 * showing just the base figure. Kept pure (no JSX) so the Estimate pane and the Prioritization pane
 * each render the figures with their own markup/tokens without re-deriving the math.
 */

import type { EstimateRange, SalesDealEstimateResult } from '@protoss/shared';
import { getEffectiveEstimateData } from '@protoss/shared';

export interface HoursRange {
  base: number;
  low: number;
  high: number;
}

/** Base hours + variance-band low/high. Null when the band is absent/unconfigured. */
export function computeHoursRange(hours: number, range?: EstimateRange | null): HoursRange | null {
  if (!range || (range.lowerPct == null && range.upperPct == null)) return null;
  const low = range.lowerPct != null ? Math.round(hours * (1 - range.lowerPct / 100)) : hours;
  const high = range.upperPct != null ? Math.round(hours * (1 + range.upperPct / 100)) : hours;
  return { base: hours, low, high };
}

/**
 * Map every line-item fieldKey to its estimated base hours from the saved estimate (overrides
 * applied via getEffectiveEstimateData). Indexed by both `sourceFieldKeys` and `subsectionKey` so
 * the lookup is robust to how the ad-hoc scope section names its subsections. Null estimate => empty
 * map => every row shows "-".
 */
export function buildHoursByField(estimate: SalesDealEstimateResult | null): Map<string, number> {
  const out = new Map<string, number>();
  if (!estimate) return out;
  const effective = getEffectiveEstimateData(estimate);
  for (const section of effective.sections) {
    for (const sub of section.subsections) {
      out.set(sub.subsectionKey, sub.hours);
      for (const key of sub.sourceFieldKeys ?? []) out.set(key, sub.hours);
    }
  }
  return out;
}
